import PropTypes from 'prop-types';
import React, { useEffect, useState, useMemo } from 'react';
import BorderOutlined from '@ant-design/icons/BorderOutlined';
import DownOutlined from '@ant-design/icons/DownOutlined';
import UpOutlined from '@ant-design/icons/UpOutlined';
import RightOutlined from '@ant-design/icons/RightOutlined';
import { useLocation } from 'react-router';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Collapse from '@mui/material/Collapse';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { MenuOrientation, ThemeMode } from 'src/config';
import useConfig from 'src/hooks/useConfig';

import NavItem from 'src/components/ui/NavItem';
import Dot from 'src/components/ui/Dot';
import IconButton from 'src/components/ui/IconButton';
import SimpleBar from 'src/components/ui/SimpleBar';
import Transitions from 'src/components/ui/Transitions';
import RouterLink from 'src/components/ui/RouterLink';
import useInterface from 'src/hooks/useInterface';
import { isMenuUrlActive } from 'src/utils/helpers/menuUrl';

import PopperStyled from './styled/PopperStyled';

const getMenuPathname = (menuUrl) => {
	if (!menuUrl) return null;

	try {
		return new URL(menuUrl, 'http://local.invalid').pathname;
	} catch {
		return null;
	}
};

const hasActiveDescendant = (items, location) =>
	items?.some((item) => {
		if (
			item.children?.length &&
			hasActiveDescendant(item.children, location)
		) {
			return true;
		}

		const itemUrl = item.link || item.url;

		if (isMenuUrlActive(itemUrl, location)) return true;

		return getMenuPathname(itemUrl) === location.pathname;
	}) ?? false;

const NavCollapse = ({
	menu,
	level,
	parentId,
	setSelectedItems,
	selectedItems,
	setSelectedLevel,
	selectedLevel,
	drawerOpen: drawerOpenProp,
	navigation,
	pathname: pathnameProp,
	search: searchProp,
}) => {
	const { isDrawerOpen } = useInterface();
	const drawerOpen =
		drawerOpenProp === undefined ? isDrawerOpen : drawerOpenProp;
	const theme = useTheme();
	const downLG = useMediaQuery(theme.breakpoints.down('lg'));
	const { mode, menuOrientation } = useConfig();
	const routerLocation = useLocation();
	const location = {
		pathname: pathnameProp ?? routerLocation.pathname,
		search: searchProp ?? routerLocation.search,
	};

	const [open, setOpen] = useState(false);
	const [selected, setSelected] = useState(null);
	const [anchorEl, setAnchorEl] = useState(null);

	const [anchorElCollapse, setAnchorElCollapse] = React.useState(null);

	const isRouteActive =
		isMenuUrlActive(menu.url, location) ||
		hasActiveDescendant(menu.children, location);
	const collapseOpen = isRouteActive || open;

	const openCollapse = Boolean(anchorElCollapse);
	const handleClickCollapse = (event) => {
		setAnchorElCollapse(event.currentTarget);
	};
	const handleCloseCollapse = () => {
		setAnchorElCollapse(null);
	};

	const handleClick = (event, isRedirect) => {
		setAnchorEl(null);
		setSelectedLevel(level);
		if (drawerOpen) {
			setOpen(!open);
			setSelected(!selected ? menu.id : null);
			setSelectedItems(!selected ? menu.id : '');
			if (menu.url && isRedirect) navigation(`${menu.url}`);
		} else {
			setAnchorEl(event?.currentTarget);
		}
	};

	const handlerIconLink = () => {
		if (!drawerOpen) {
			if (menu.url) navigation(`${menu.url}`);
			setSelected(menu.id);
		}
	};

	const handleHover = (event) => {
		setAnchorEl(event?.currentTarget);
	};

	const miniMenuOpened = Boolean(anchorEl);

	const handleClose = () => {
		setOpen(false);
		if (!miniMenuOpened) {
			if (!menu.url) {
				setSelected(null);
			}
		}
		setAnchorEl(null);
	};

	useMemo(() => {
		if (isRouteActive) return;

		if (selected === selectedItems) {
			if (level === 1) {
				setOpen(true);
			}
		} else {
			if (level === selectedLevel) {
				setOpen(false);
				if (!miniMenuOpened && !drawerOpen && !selected) {
					setSelected(null);
				}
				if (drawerOpen) {
					setSelected(null);
				}
			}
		}
	}, [
		isRouteActive,
		selectedItems,
		level,
		selected,
		miniMenuOpened,
		drawerOpen,
		selectedLevel,
	]);

	// Keep parent expanded and marked when a child matches the current URL
	useEffect(() => {
		if (isRouteActive) {
			setSelected(menu.id);
			setOpen(true);
			setSelectedItems?.(menu.id);
			setSelectedLevel?.(level);
			if (isMenuUrlActive(menu.url, location)) setAnchorEl(null);
			return;
		}

		if (!miniMenuOpened) {
			setSelected(null);
			setOpen(false);
		} else {
			setAnchorEl(null);
		}
		// Only sync to location — ignore menu data refreshes (counts/chips)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.pathname, location.search]);

	const navCollapse = menu.children?.map((item) => {
		switch (item.type) {
			case 'collapse':
				return (
					<NavCollapse
						key={item.id}
						setSelectedItems={setSelectedItems}
						setSelectedLevel={setSelectedLevel}
						selectedLevel={selectedLevel}
						selectedItems={selectedItems}
						menu={item}
						level={level + 1}
						parentId={parentId}
					/>
				);
			case 'item':
				return <NavItem key={item.id} item={item} level={level + 1} />;
			default:
				return (
					<Typography
						key={item.id}
						variant="h6"
						color="error"
						align="center"
					>
						Fix - Collapse or Item
					</Typography>
				);
		}
	});

	const isSelected = selected === menu.id || isRouteActive;
	const borderIcon =
		level === 1 ? <BorderOutlined style={{ fontSize: '1rem' }} /> : false;
	const Icon = menu.icon;
	const menuIcon = menu.icon ? (
		<Icon style={{ fontSize: drawerOpen ? '1rem' : '1.25rem' }} />
	) : (
		borderIcon
	);
	const textColor = mode === ThemeMode.DARK ? 'grey.400' : 'text.primary';
	const iconSelectedColor =
		mode === ThemeMode.DARK && drawerOpen ? 'text.primary' : 'primary.main';
	const popperId = miniMenuOpened ? `collapse-pop-${menu.id}` : undefined;
	const FlexBox = {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		width: '100%',
	};

	return (
		<>
			{menuOrientation === MenuOrientation.VERTICAL || downLG ? (
				<>
					<ListItemButton
						id={`${menu.id}-button`}
						selected={selected === menu.id || isRouteActive}
						{...(!drawerOpen && {
							onMouseEnter: (e) => handleClick(e, true),
							onMouseLeave: handleClose,
						})}
						onClick={(e) => handleClick(e, true)}
						sx={{
							pl: drawerOpen ? `${level * 28}px` : 1.5,
							py: !drawerOpen && level === 1 ? 1.25 : 1,
							...(drawerOpen && {
								'&:hover': {
									bgcolor:
										mode === ThemeMode.DARK
											? 'divider'
											: 'primary.lighter',
								},
								'&.Mui-selected': {
									bgcolor: 'transparent',
									color: iconSelectedColor,
									'&:hover': {
										color: iconSelectedColor,
										bgcolor:
											mode === ThemeMode.DARK
												? 'divider'
												: 'transparent',
									},
								},
							}),
							...(!drawerOpen && {
								'&:hover': { bgcolor: 'transparent' },
								'&.Mui-selected': {
									'&:hover': { bgcolor: 'transparent' },
									bgcolor: 'transparent',
								},
							}),
						}}
						{...(drawerOpen &&
							menu.isDropdown && {
							'aria-controls': openCollapse
								? `${menu.id}-menu`
								: undefined,
							'aria-haspopup': true,
							'aria-expanded': openCollapse
								? 'true'
								: undefined,
							onClick: handleClickCollapse,
						})}
					>
						{menuIcon && (
							<ListItemIcon
								onClick={handlerIconLink}
								sx={{
									minWidth: 28,
									color:
										selected === menu.id || isRouteActive
											? 'primary.main'
											: textColor,
									...(!drawerOpen && {
										borderRadius: 1.5,
										width: 36,
										height: 36,
										alignItems: 'center',
										justifyContent: 'center',
										'&:hover': {
											bgcolor:
												mode === ThemeMode.DARK
													? 'secondary.light'
													: 'secondary.lighter',
										},
									}),
									...(!drawerOpen &&
										(selected === menu.id ||
											isRouteActive) && {
										bgcolor:
												mode === ThemeMode.DARK
													? 'primary.900'
													: 'primary.lighter',
										'&:hover': {
											bgcolor:
													mode === ThemeMode.DARK
														? 'primary.darker'
														: 'primary.lighter',
										},
									}),
								}}
							>
								{menuIcon}
							</ListItemIcon>
						)}
						{(drawerOpen || (!drawerOpen && level !== 1)) && (
							<ListItemText
								primary={
									<Typography
										variant="h6"
										color={
											selected === menu.id ||
											isRouteActive
												? 'primary'
												: textColor
										}
									>
										{menu.title}
									</Typography>
								}
								secondary={
									menu.caption && (
										<Typography
											variant="caption"
											color="secondary"
										>
											{menu.caption}
										</Typography>
									)
								}
							/>
						)}

						{(drawerOpen || (!drawerOpen && level !== 1)) &&
							(menu?.url ? (
								<IconButton
									onClick={(event) => {
										event?.stopPropagation();
										handleClick(event, false);
									}}
									color="secondary"
									variant="outlined"
									sx={{
										width: 20,
										height: 20,
										mr: '-5px',
										color: 'secondary.dark',
										borderColor: collapseOpen
											? 'primary.light'
											: 'secondary.light',
										'&:hover': {
											borderColor: collapseOpen
												? 'primary.main'
												: 'secondary.main',
										},
									}}
								>
									{miniMenuOpened || collapseOpen ? (
										<UpOutlined
											style={{
												fontSize: '0.625rem',
												color: theme.palette.primary
													.main,
											}}
										/>
									) : (
										<DownOutlined
											style={{ fontSize: '0.625rem' }}
										/>
									)}
								</IconButton>
							) : (
								<>
									{miniMenuOpened || collapseOpen ? (
										<UpOutlined
											style={{
												fontSize: '0.625rem',
												marginLeft: 1,
												color: theme.palette.primary
													.main,
											}}
										/>
									) : (
										<DownOutlined
											style={{
												fontSize: '0.625rem',
												marginLeft: 1,
											}}
										/>
									)}
								</>
							))}

						{!drawerOpen && (
							<PopperStyled
								open={miniMenuOpened}
								anchorEl={anchorEl}
								placement="right-start"
								style={{ zIndex: 2001 }}
								popperOptions={{
									modifiers: [
										{
											name: 'offset',
											options: { offset: [-12, 1] },
										},
									],
								}}
							>
								{({ TransitionProps }) => (
									<Transitions
										in={miniMenuOpened}
										{...TransitionProps}
									>
										<Paper
											sx={{
												overflow: 'hidden',
												mt: 1.5,
												boxShadow:
													theme.customShadows.z1,
												backgroundImage: 'none',
												border: '1px solid',
												borderColor: 'divider',
											}}
										>
											<ClickAwayListener
												onClickAway={handleClose}
											>
												<>
													<SimpleBar
														sx={{
															overflowX: 'hidden',
															overflowY: 'auto',
															maxHeight: '50vh',
														}}
													>
														{navCollapse}
													</SimpleBar>
												</>
											</ClickAwayListener>
										</Paper>
									</Transitions>
								)}
							</PopperStyled>
						)}
					</ListItemButton>
					{drawerOpen && !menu?.isDropdown && (
						<Collapse
							in={collapseOpen}
							timeout="auto"
							unmountOnExit
						>
							<List sx={{ p: 0 }}>{navCollapse}</List>
						</Collapse>
					)}

					{drawerOpen && menu?.isDropdown && (
						<Menu
							id={`${menu.id}-menu`}
							aria-labelledby={`${menu.id}-button`}
							anchorEl={anchorElCollapse}
							open={openCollapse}
							onClose={handleCloseCollapse}
							onClick={handleCloseCollapse}
							anchorOrigin={{
								vertical: 'bottom',
								horizontal: 'right',
							}}
							transformOrigin={{
								vertical: 'top',
								horizontal: 'right',
							}}
							sx={{
								'& .MuiPaper-root': {
									boxShadow: theme.shadows[2],
								},
								'& .MuiListItemButton-root': { pl: 2 },
							}}
						>
							{navCollapse}
						</Menu>
					)}
				</>
			) : (
				<ListItemButton
					{...(menu?.url && { component: RouterLink, to: menu.url })}
					id={`boundary-${popperId}`}
					disableRipple
					selected={isSelected}
					onMouseEnter={handleHover}
					onMouseLeave={handleClose}
					onClick={handleHover}
					aria-describedby={popperId}
					sx={{ '&.Mui-selected': { bgcolor: 'transparent' } }}
				>
					<Box onClick={handlerIconLink} sx={FlexBox}>
						{menuIcon && (
							<ListItemIcon
								sx={{
									my: 'auto',
									minWidth: !menu.icon ? 18 : 28,
									color: 'secondary.dark',
								}}
							>
								{menuIcon}
							</ListItemIcon>
						)}
						{!menuIcon && level !== 1 && (
							<ListItemIcon
								sx={{
									my: 'auto',
									minWidth: !menu.icon ? 18 : 28,
									bgcolor: 'transparent',
									'&:hover': { bgcolor: 'transparent' },
								}}
							>
								<Dot
									size={4}
									color={isSelected ? 'primary' : 'secondary'}
								/>
							</ListItemIcon>
						)}
						<ListItemText
							primary={
								<Typography
									variant="body1"
									color="inherit"
									sx={{ my: 'auto' }}
								>
									{menu.title}
								</Typography>
							}
						/>
						{miniMenuOpened ? <RightOutlined /> : <DownOutlined />}
					</Box>

					{anchorEl && (
						<PopperStyled
							id={popperId}
							open={miniMenuOpened}
							anchorEl={anchorEl}
							placement="right-start"
							style={{ zIndex: 2001 }}
							modifiers={[
								{
									name: 'offset',
									options: { offset: [-10, 0] },
								},
							]}
						>
							{({ TransitionProps }) => (
								<Transitions
									in={miniMenuOpened}
									{...TransitionProps}
								>
									<Paper
										sx={{
											overflow: 'hidden',
											mt: 1.5,
											py: 0.5,
											boxShadow: theme.shadows[8],
											backgroundImage: 'none',
										}}
									>
										<ClickAwayListener
											onClickAway={handleClose}
										>
											<>
												<SimpleBar
													sx={{
														overflowX: 'hidden',
														overflowY: 'auto',
														maxHeight: '50vh',
													}}
												>
													{navCollapse}
												</SimpleBar>
											</>
										</ClickAwayListener>
									</Paper>
								</Transitions>
							)}
						</PopperStyled>
					)}
				</ListItemButton>
			)}
		</>
	);
};

NavCollapse.propTypes = {
	menu: PropTypes.any,
	level: PropTypes.number,
	parentId: PropTypes.string,
	setSelectedItems: PropTypes.oneOfType([PropTypes.func, PropTypes.any]),
	selectedItems: PropTypes.oneOfType([PropTypes.any, PropTypes.string]),
	setSelectedLevel: PropTypes.func,
	selectedLevel: PropTypes.number,
	drawerOpen: PropTypes.bool,
	navigation: PropTypes.func,
	pathname: PropTypes.string,
	search: PropTypes.string,
};

export default NavCollapse;
